import * as ko from 'knockout';
import { CfbGameRecord } from './game-record';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { IGame } from '../../shared/interfaces/IGame';
import type { IGameRecord } from '../../shared/interfaces/IGameRecord';

export class CfbGameRecords implements IGameRecords {
  AllRecord: IGameRecord;
  DivisionRecord: IGameRecord;
  ConferenceRecord: IGameRecord;
  HomeRecord: IGameRecord;
  AwayRecord: IGameRecord;
  WinningTeamsRecord: KnockoutComputed<IGameRecord>;
  LosingTeamsRecord: KnockoutComputed<IGameRecord>;
  Wins: KnockoutObservableArray<IGame>;
  Losses: KnockoutObservableArray<IGame>;
  Ties: KnockoutObservableArray<IGame>;
  teamName: string;
  teamNameObs: KnockoutObservable<string>;
  fakeObs: KnockoutObservable<any>;
  subscribers: any[] = [];

  constructor(
    Wins: KnockoutObservableArray<IGame>,
    Losses: KnockoutObservableArray<IGame>,
    Ties: KnockoutObservableArray<IGame>,
    teamName: KnockoutObservable<string>,
    fakeObs: KnockoutObservable<any>,
  ) {
    const self = this;
    this.AllRecord = new CfbGameRecord(fakeObs);
    this.DivisionRecord = new CfbGameRecord(fakeObs);
    this.ConferenceRecord = new CfbGameRecord(fakeObs);
    this.HomeRecord = new CfbGameRecord(fakeObs);
    this.AwayRecord = new CfbGameRecord(fakeObs);
    self.teamNameObs = teamName;
    self.teamName = teamName();
    self.fakeObs = fakeObs;
    self.Wins = Wins;
    self.Losses = Losses;
    self.Ties = Ties;

    this.WinningTeamsRecord = ko.computed(() => {
      const record = new CfbGameRecord(fakeObs);
      ko.utils.arrayForEach(Wins(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) > 0.5) record.Wins.increment();
      });
      ko.utils.arrayForEach(Losses(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) > 0.5) record.Losses.increment();
      });
      ko.utils.arrayForEach(Ties(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) > 0.5) record.Ties.increment();
      });
      return record;
    });

    this.LosingTeamsRecord = ko.computed(() => {
      const record = new CfbGameRecord(fakeObs);
      ko.utils.arrayForEach(Wins(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) <= 0.5) record.Wins.increment();
      });
      ko.utils.arrayForEach(Losses(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) <= 0.5) record.Losses.increment();
      });
      ko.utils.arrayForEach(Ties(), (item: any) => {
        if (this.getOppPct(self.teamNameObs(), item) <= 0.5) record.Ties.increment();
      });
      return record;
    });
  }

  initSubscribers() {
    const self = this;
    self.Wins.subscribe((arr) => {
      self.fakeObs();
      self.resetWins();
      self.AllRecord.Wins(arr.length);
      ko.utils.arrayForEach(arr, (item: any) => {
        const home = item.home, away = item.away;
        if (home.Division === away.Division && home.Conference === away.Conference) {
          self.DivisionRecord.Wins.increment();
        }
        if (home.Conference === away.Conference && item.isConferenceGame) {
          self.ConferenceRecord.Wins.increment();
        }
        if (home.TeamName === self.teamNameObs()) self.HomeRecord.Wins.increment();
        if (away.TeamName === self.teamNameObs()) self.AwayRecord.Wins.increment();
      });
    });

    self.Losses.subscribe((arr) => {
      self.fakeObs();
      self.resetLosses();
      self.AllRecord.Losses(arr.length);
      ko.utils.arrayForEach(arr, (item: any) => {
        const home = item.home, away = item.away;
        if (home.Division === away.Division && home.Conference === away.Conference) {
          self.DivisionRecord.Losses.increment();
        }
        if (home.Conference === away.Conference && item.isConferenceGame) {
          self.ConferenceRecord.Losses.increment();
        }
        if (home.TeamName === self.teamNameObs()) self.HomeRecord.Losses.increment();
        if (away.TeamName === self.teamNameObs()) self.AwayRecord.Losses.increment();
      });
    });

    self.Ties.subscribe((arr) => {
      self.fakeObs();
      self.resetTies();
      self.AllRecord.Ties(arr.length);
      ko.utils.arrayForEach(arr, (item: any) => {
        const home = item.home, away = item.away;
        if (home.Division === away.Division && home.Conference === away.Conference) {
          self.DivisionRecord.Ties.increment();
        }
        if (home.Conference === away.Conference && item.isConferenceGame) {
          self.ConferenceRecord.Ties.increment();
        }
        if (home.TeamName === self.teamNameObs()) self.HomeRecord.Ties.increment();
        if (away.TeamName === self.teamNameObs()) self.AwayRecord.Ties.increment();
      });
    });

    self.Wins.valueHasMutated();
    self.Losses.valueHasMutated();
    self.Ties.valueHasMutated();
  }

  valueHasMutated() {
    this.Wins.valueHasMutated();
    this.Losses.valueHasMutated();
    this.Ties.valueHasMutated();
  }

  getOppPct(teamName: string, game: IGame): number {
    const home = (game as any).home;
    if (home.TeamName === teamName) {
      return (game as any).away.Pct();
    }
    return home.Pct();
  }

  resetWins() {
    this.AllRecord.resetWins();
    this.DivisionRecord.resetWins();
    this.ConferenceRecord.resetWins();
    this.HomeRecord.resetWins();
    this.AwayRecord.resetWins();
  }

  resetLosses() {
    this.AllRecord.resetLosses();
    this.DivisionRecord.resetLosses();
    this.ConferenceRecord.resetLosses();
    this.HomeRecord.resetLosses();
    this.AwayRecord.resetLosses();
  }

  resetTies() {
    this.AllRecord.resetTies();
    this.DivisionRecord.resetTies();
    this.ConferenceRecord.resetTies();
    this.HomeRecord.resetTies();
    this.AwayRecord.resetTies();
  }
}
